<template>
  <div class="opportunity-quotes-page">
    <OpportunityHeader />
    <BaseLoadingBar v-if="opportunityPending" />

    <div class="opportunity-quotes-page__table-container" v-else>
      <div class="opportunity-quotes-page__header">
        <div
          v-show="!opportunityQuotesPending && !unassignedPotentialBiddersPending && !tenderPending"
          class="opportunity-quotes-page__count"
        >
          <span class="number-monospace">{{ quotes.length }}</span> {{ $tc('opportunity-quotes.quote', quotes.length) }}
          <span>
            | <span class="number-monospace">{{ unassignedPotentialBidders.length }}</span> Suggestions</span
          >
        </div>
      </div>
      <QuotesDataTable v-if="opportunity && quotes" ref="quotesDataTable"> </QuotesDataTable>
    </div>
  </div>
</template>

<script>
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import OpportunityHeader from '@/components/page-opportunities/OpportunityHeader';
import QuotesDataTable from '@/components/page-opportunity-quotes/QuotesDataTable.vue';

import { paths } from '@/util/paths';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PageOpportunityQuotes',
  components: {
    BaseLoadingBar,
    OpportunityHeader,
    QuotesDataTable,
  },
  props: {
    opportunityId: String,
  },
  data() {
    return {
      syncInterval: null,
    };
  },
  computed: {
    ...mapGetters('tenantModules', ['tenantModules']),
    ...mapGetters('tender', ['tender']),
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('tender', { tenderPending: 'pending' }),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('opportunity', { opportunityPending: 'pending' }),
    ...mapGetters('opportunitySyncProfiles', ['opportunitySyncProfiles']),
    ...mapGetters('tenantModules', ['tenantModules']),
    ...mapGetters('quotes', ['quotes']),
    ...mapGetters('quotes', { opportunityQuotesPending: 'pending' }),
    ...mapGetters('quoteSync', ['quoteSyncs']),
    ...mapGetters('unassignedPotentialBidders', ['unassignedPotentialBidders']),
    ...mapGetters('unassignedPotentialBidders', { unassignedPotentialBiddersPending: 'pending' }),
    activeSyncProfiles() {
      return this.opportunitySyncProfiles.filter((profile) => profile.active);
    },
    hasInProgressSyncs() {
      return this.quoteSyncs.some((sync) => sync.status === 'IN_PROGRESS');
    },
  },
  methods: {
    ...mapActions('opportunity', ['getOpportunity']),
    ...mapActions('opportunitySyncProfiles', ['getOpportunitySyncProfiles']),
    ...mapActions('quotes', ['getQuotes']),
    ...mapActions('quoteSync', ['getQuoteSyncs']),
    ...mapActions('opportunitySync', ['getOpportunitySyncs']),
    handleError(error) {
      if (!error.response) this.$router.replace(paths.notFound);

      // If a not found or forbidden error occurs, redirect to not-found page
      if (error.response.status === 404 || error.response.status === 403) {
        this.$router.replace(paths.notFound);
      } else {
        // Otherwise show an error toast
        console.error(error);
        this.$toast.error(error);
        throw error;
      }
    },
    fetchOpportunitySyncs() {
      let profileIds = this.activeSyncProfiles.map((profile) => profile.id);
      if (!profileIds.length || !this.opportunity) return Promise.resolve();

      return this.getOpportunitySyncs({ profileIds, opportunityIds: [this.opportunity.id] });
    },
    handleInProgressSyncs() {
      clearInterval(this.syncInterval);
      if (this.hasInProgressSyncs) {
        this.syncInterval = setInterval(() => {
          this.getQuoteSyncs({
            profileIds: this.activeSyncProfiles.map((profile) => profile.id),
            quoteIds: this.quotes.map((quote) => quote.id),
          });
        }, 10000);
      }
    },
  },
  watch: {
    hasInProgressSyncs() {
      this.handleInProgressSyncs();
    },
  },
  async created() {
    try {
      const opportunityId = this.opportunityId;

      if (!this.opportunity || this.opportunity.id !== opportunityId) {
        const opportunity = await this.getOpportunity({ id: opportunityId });
        if (!opportunity) return;
      }

      this.getQuotes({ opportunityId });

      await this.getOpportunitySyncProfiles({});
      await this.fetchOpportunitySyncs();
      await this.getQuoteSyncs({
        profileIds: this.activeSyncProfiles.map((profile) => profile.id),
        quoteIds: this.quotes.map((quote) => quote.id),
      });
    } catch (error) {
      this.handleError(error);
    }
  },
  beforeDestroy() {
    clearInterval(this.syncInterval);
  },
};
</script>

<style lang="scss">
.opportunity-quotes-page {
  // At least the screen, as tall as its quotes: the header stays pinned to the end of a long list.
  display: flex;
  flex-direction: column;
  min-height: calc(var(--viewport-height) - 56px);
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 1rem;
    align-items: center;
  }

  &__count {
    color: $color-neutral;
    font-size: $font-size-small;
    text-transform: uppercase;
  }

  &__table-container {
    flex: 1 0 auto;
    padding: 3rem;
    background: $color-slate-100;
  }
}
</style>
